import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  CpuChipIcon,
  CloudIcon,
  ShieldCheckIcon,
  CogIcon,
  RocketLaunchIcon,
  CircleStackIcon,
  CubeTransparentIcon,
  BuildingOffice2Icon,
  ArrowPathIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  SparklesIcon,
  DevicePhoneMobileIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  CodeBracketIcon,
  BeakerIcon,
  LifebuoyIcon,
} from '@heroicons/react/24/outline';

import cloudImg from '../../assets/img/Recent/cloud.png';
import mobileImg from '../../assets/img/Recent/Mobilebanking.png';
import analyticsImg from '../../assets/img/Recent/data analysis.png';
import programmingImg from '../../assets/img/Recent/Programing.png';
import cloudMigrationImg from '../../assets/img/Recent/cloud migration.png';
import ecommerceImg from '../../assets/img/Recent/ecommerce.png';

const categories = ['All', 'Intelligence', 'Cloud', 'Security', 'Engineering', 'Transformation'];

const servicesList = [
  {
    id: 'ai',
    name: 'AI & Machine Learning',
    category: 'Intelligence',
    shortDescription: 'Intelligent automation, predictive models, and AI-driven decision systems.',
    fullDescription: 'We design and deploy custom AI solutions—from NLP and computer vision to predictive analytics—that automate workflows, unlock insights, and create competitive advantage for your organization.',
    icon: CpuChipIcon,
    image: analyticsImg,
    href: '/services#ai',
    benefits: ['Custom ML model development', 'Predictive analytics & forecasting', 'Process automation at scale', 'NLP & computer vision solutions'],
    features: ['MLOps pipelines', 'Model monitoring & retraining', 'Responsible AI practices', 'Integration with existing data stacks'],
  },
  {
    id: 'cloud',
    name: 'Cloud Infrastructure',
    category: 'Cloud',
    shortDescription: 'Scalable multi-cloud architectures with high availability and resilience.',
    fullDescription: 'Our cloud architects design, migrate, and optimize AWS, Azure, and Google Cloud environments for performance, security, and cost efficiency—with minimal disruption to your operations.',
    icon: CloudIcon,
    image: cloudImg,
    href: '/cloud-solutions',
    benefits: ['Seamless cloud migration', 'Multi-cloud & hybrid strategies', 'Auto-scaling & high availability', 'Cost optimization & FinOps'],
    features: ['Kubernetes orchestration', 'Serverless architectures', 'Disaster recovery planning', '24/7 infrastructure monitoring'],
  },
  {
    id: 'security',
    name: 'Cybersecurity',
    category: 'Security',
    shortDescription: 'Zero-trust security, threat detection, and enterprise compliance frameworks.',
    fullDescription: 'Protect your organization with layered security—audits, penetration testing, SOC monitoring, and compliance programs aligned with industry standards and regulatory requirements.',
    icon: ShieldCheckIcon,
    image: cloudMigrationImg,
    href: '/cybersecurity',
    benefits: ['Security audits & assessments', 'Threat detection & response', 'Regulatory compliance support', 'Employee security training'],
    features: ['Zero-trust architecture', 'SIEM & incident response', 'Vulnerability management', 'Managed security services'],
  },
  {
    id: 'devops',
    name: 'DevOps Automation',
    category: 'Engineering',
    shortDescription: 'CI/CD pipelines, infrastructure as code, and automated deployments.',
    fullDescription: 'Accelerate delivery with automated pipelines, IaC, and DevSecOps practices that improve release velocity while maintaining security and reliability across your software lifecycle.',
    icon: CogIcon,
    image: cloudImg,
    href: '/services#devops',
    benefits: ['Faster release cycles', 'Reduced manual errors', 'Infrastructure as code', 'Integrated security scanning'],
    features: ['CI/CD pipeline design', 'Terraform & Ansible', 'Container orchestration', 'Observability & logging'],
  },
  {
    id: 'saas',
    name: 'SaaS Development',
    category: 'Engineering',
    shortDescription: 'Cloud-native SaaS platforms built for scale, security, and growth.',
    fullDescription: 'We build multi-tenant SaaS products with subscription billing, role-based access, analytics dashboards, and APIs designed for rapid iteration and global scale.',
    icon: RocketLaunchIcon,
    image: ecommerceImg,
    href: '/services#saas',
    benefits: ['Multi-tenant architecture', 'Subscription & billing integration', 'Scalable API design', 'Rapid feature delivery'],
    features: ['React / Node.js stacks', 'Microservices design', 'Usage analytics', 'SOC2-ready security patterns'],
  },
  {
    id: 'data',
    name: 'Data Engineering',
    category: 'Intelligence',
    shortDescription: 'Modern data pipelines, warehouses, and real-time analytics platforms.',
    fullDescription: 'Transform raw data into actionable intelligence with modern ETL/ELT pipelines, data lakes, warehouses, and real-time streaming architectures tailored to your business.',
    icon: CircleStackIcon,
    image: analyticsImg,
    href: '/services#data',
    benefits: ['Unified data platforms', 'Real-time analytics', 'Data quality & governance', 'Executive dashboards'],
    features: ['Snowflake / BigQuery / Redshift', 'Apache Spark & Kafka', 'dbt transformations', 'BI tool integration'],
  },
  {
    id: 'blockchain',
    name: 'Blockchain Solutions',
    category: 'Engineering',
    shortDescription: 'Secure distributed ledgers and decentralized enterprise applications.',
    fullDescription: 'Explore enterprise blockchain use cases—from supply chain traceability to secure transactions—with solutions that balance innovation, compliance, and practical business value.',
    icon: CubeTransparentIcon,
    image: programmingImg,
    href: '/services#blockchain',
    benefits: ['Transparent audit trails', 'Secure transaction layers', 'Smart contract development', 'Enterprise-grade governance'],
    features: ['Private & consortium chains', 'Wallet & identity systems', 'Integration APIs', 'Compliance consulting'],
  },
  {
    id: 'enterprise',
    name: 'Enterprise Software',
    category: 'Engineering',
    shortDescription: 'Custom ERP, CRM, and mission-critical business applications.',
    fullDescription: 'We develop robust enterprise applications that streamline operations, integrate with legacy systems, and support thousands of users with exceptional performance and reliability.',
    icon: BuildingOffice2Icon,
    image: programmingImg,
    href: '/services#enterprise',
    benefits: ['Tailored to your workflows', 'Legacy system integration', 'Role-based access control', 'Long-term maintainability'],
    features: ['Agile delivery methodology', 'API-first architecture', 'Automated testing suites', 'Dedicated support SLAs'],
  },
  {
    id: 'mobile',
    name: 'Web & Mobile Development',
    category: 'Engineering',
    shortDescription: 'Responsive web apps and native mobile experiences that convert.',
    fullDescription: 'Our designers and engineers craft pixel-perfect, performant web and mobile applications with intuitive UX, accessibility, and seamless cross-device experiences.',
    icon: DevicePhoneMobileIcon,
    image: mobileImg,
    href: '/web-development',
    benefits: ['Cross-platform reach', 'Premium UI/UX design', 'Fast performance scores', 'SEO & accessibility built-in'],
    features: ['React & React Native', 'Progressive web apps', 'E-commerce platforms', 'API & third-party integrations'],
  },
  {
    id: 'transform',
    name: 'Digital Transformation',
    category: 'Transformation',
    shortDescription: 'End-to-end modernization strategies for future-ready organizations.',
    fullDescription: 'Partner with our consultants to reimagine processes, adopt modern technology stacks, and drive organization-wide change with clear roadmaps and measurable outcomes.',
    icon: ArrowPathIcon,
    image: cloudMigrationImg,
    href: '/training-consulting',
    benefits: ['Technology roadmaps', 'Process digitization', 'Change management support', 'Measurable ROI tracking'],
    features: ['Current-state assessments', 'Vendor evaluation', 'Pilot program design', 'Staff enablement & training'],
  },
];

const processSteps = [
  { id: 1, title: 'Discovery', description: 'Deep-dive workshops to understand goals, constraints, and success metrics.', icon: MagnifyingGlassIcon },
  { id: 2, title: 'Strategy', description: 'Architecture blueprints, timelines, and investment plans tailored to you.', icon: ChartBarIcon },
  { id: 3, title: 'Design', description: 'UX prototypes and technical specs validated with stakeholders.', icon: SparklesIcon },
  { id: 4, title: 'Development', description: 'Agile sprints with transparent progress and continuous feedback.', icon: CodeBracketIcon },
  { id: 5, title: 'Testing', description: 'QA, security testing, and performance validation before launch.', icon: BeakerIcon },
  { id: 6, title: 'Launch & Support', description: 'Deployment, monitoring, and ongoing optimization for long-term success.', icon: LifebuoyIcon },
];

const testimonials = [
  { quote: 'Nisunajni delivered our cloud migration ahead of schedule with zero downtime. Exceptional engineering discipline.', author: 'Sarah Johnson', role: 'CTO, TechVision Inc.', initials: 'SJ', color: 'from-blue-500 to-cyan-500' },
  { quote: 'Their AI platform increased operational efficiency by 40%. A strategic partner, not just a vendor.', author: 'Michael Chen', role: 'IT Director, Global Finance', initials: 'MC', color: 'from-violet-500 to-purple-500' },
  { quote: 'Cybersecurity audits uncovered critical gaps we missed. Our posture is enterprise-grade now.', author: 'Emily Rodriguez', role: 'Director of IT, SecureNet', initials: 'ER', color: 'from-emerald-500 to-teal-500' },
];

const faqData = [
  { question: 'How long does a typical project take?', answer: 'Timelines depend on scope. MVPs often ship in 8–12 weeks; enterprise platforms may run 6–12+ months. We provide detailed estimates after discovery.' },
  { question: 'Which technologies do you specialize in?', answer: 'We work across React, Node.js, Python, cloud platforms (AWS, Azure, GCP), Kubernetes, AI/ML frameworks, and modern data stacks—choosing the best fit for your goals.' },
  { question: 'Do you offer post-launch support?', answer: 'Yes. We offer flexible support and maintenance plans including monitoring, security patches, performance tuning, and feature enhancements.' },
  { question: 'How do you handle security and compliance?', answer: 'Security is embedded from day one—threat modeling, secure SDLC, encryption, access controls, and alignment with standards like ISO 27001 and industry regulations.' },
  { question: 'Can you integrate with our existing systems?', answer: 'Absolutely. API-first design and proven integration patterns let us connect new solutions with ERP, CRM, legacy databases, and third-party services.' },
];

function SectionLabel({ children }) {
  return (
    <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase rounded-full hero-glass-tag text-[#2563EB]">
      {children}
    </span>
  );
}

export default function Services() {
  const [refHero, inViewHero] = useInView({ threshold: 0.1, triggerOnce: true });
  const [refServices, inViewServices] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refProcess, inViewProcess] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refFaq, inViewFaq] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refCta, inViewCta] = useInView({ threshold: 0.1, triggerOnce: true });

  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedService, setSelectedService] = useState(servicesList[0]);
  const [openFaq, setOpenFaq] = useState(null);

  const filtered =
    activeCategory === 'All'
      ? servicesList
      : servicesList.filter((s) => s.category === activeCategory);

  useEffect(() => {
    if (!filtered.find((s) => s.id === selectedService.id)) {
      setSelectedService(filtered[0] || servicesList[0]);
    }
  }, [activeCategory]);

  return (
    <div className="bg-white dark:bg-gray-900 text-slate-900 dark:text-white overflow-x-hidden">
      {/* Hero */}
      <section
        ref={refHero}
        className="relative pt-28 pb-20 md:pt-32 md:pb-28 px-6 bg-gradient-to-b from-white via-slate-50/90 to-gray-100/80 overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-300/10 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inViewHero ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <SectionLabel>Enterprise IT Services</SectionLabel>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            <span className="text-slate-900 dark:text-white">Advanced Solutions for </span>
            <span className="bg-gradient-to-r from-[#2563EB] via-blue-500 to-cyan-500 bg-clip-text text-transparent">
              Digital Excellence
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 dark:text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            From AI and cloud to cybersecurity and SaaS—we engineer secure, scalable technology that accelerates your business.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#services-grid"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white font-semibold rounded-xl hero-glow hover:-translate-y-0.5 transition-all"
            >
              Explore Services <ArrowRightIcon className="w-5 h-5" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-gray-200 font-medium hover:border-[#2563EB]/40 hover:text-[#2563EB] transition-all hero-glass-tag"
            >
              Get a Consultation
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Services — bento + detail panel */}
      <section id="services-grid" ref={refServices} className="py-20 md:py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inViewServices ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-12"
          >
            <SectionLabel>What We Offer</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Our <span className="text-[#2563EB]">Services</span>
            </h2>
            <p className="text-slate-600 dark:text-gray-300 max-w-2xl mx-auto">
              Select a category to explore capabilities, or click any service for full details.
            </p>
          </motion.div>

          {/* Category pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-12 p-1.5 rounded-2xl hero-glass-card w-fit mx-auto">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`relative px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  activeCategory === cat ? 'text-[#2563EB]' : 'text-slate-500 hover:text-slate-800 dark:hover:text-gray-200'
                }`}
              >
                {activeCategory === cat && (
                  <motion.div
                    layoutId="services-cat-pill"
                    className="absolute inset-0 bg-white dark:bg-gray-700 rounded-xl shadow-sm border border-slate-100 dark:border-gray-600"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{cat}</span>
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            {/* Service cards */}
            <div className="lg:col-span-5 grid sm:grid-cols-2 lg:grid-cols-1 gap-4 max-h-[720px] overflow-y-auto pr-1 scrollbar-hide">
              {filtered.map((service, i) => {
                const Icon = service.icon;
                const isActive = selectedService.id === service.id;
                return (
                  <motion.button
                    key={service.id}
                    initial={{ opacity: 0, x: -16 }}
                    animate={inViewServices ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: i * 0.04 }}
                    onClick={() => setSelectedService(service)}
                    className={`group text-left w-full rounded-2xl p-5 transition-all duration-300 ${
                      isActive
                        ? 'hero-glass-card ring-2 ring-[#2563EB]/30 shadow-lg shadow-blue-500/10'
                        : 'hero-glass-tag hover:shadow-md hover:border-blue-200/60'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                          isActive ? 'bg-[#2563EB]/15' : 'bg-slate-100 dark:bg-gray-700'
                        }`}
                      >
                        <Icon className={`w-6 h-6 ${isActive ? 'text-[#2563EB]' : 'text-slate-500'}`} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-slate-900 dark:text-white text-sm md:text-base group-hover:text-[#2563EB] transition-colors">
                          {service.name}
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-gray-400 mt-1 line-clamp-2">{service.shortDescription}</p>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Detail panel — desktop */}
            <div className="hidden lg:block lg:col-span-7">
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedService.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                  className="hero-glass-card rounded-3xl overflow-hidden border border-slate-200/90 dark:border-gray-700 h-full"
                >
                  <ServiceDetailPanel service={selectedService} />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile detail — tap opens sticky panel hint */}
          <p className="lg:hidden text-center text-sm text-slate-500 mt-6">
            Tap a service above to view details below
          </p>
          <div className="lg:hidden mt-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedService.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="hero-glass-card rounded-3xl overflow-hidden"
              >
                <ServiceDetailPanel service={selectedService} compact />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Process */}
      <section ref={refProcess} className="py-20 md:py-28 px-6 bg-gradient-to-b from-slate-50/80 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={inViewProcess ? { opacity: 1, y: 0 } : {}} className="text-center mb-14">
            <SectionLabel>How We Work</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              Our <span className="text-[#2563EB]">Process</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={inViewProcess ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: i * 0.08 }}
                  whileHover={{ y: -6 }}
                  className="relative hero-glass-card rounded-2xl p-6 group"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2563EB] to-cyan-500 text-white text-sm font-bold flex items-center justify-center">
                      {step.id}
                    </span>
                    <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Icon className="w-5 h-5 text-[#2563EB]" />
                    </div>
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-gray-300">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <SectionLabel>Client Stories</SectionLabel>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Trusted by Leaders</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="hero-glass-card rounded-2xl p-6"
              >
                <p className="text-slate-600 dark:text-gray-300 text-sm mb-6 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.color} flex items-center justify-center text-white text-xs font-bold`}>
                    {t.initials}
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-slate-900 dark:text-white">{t.author}</p>
                    <p className="text-xs text-[#2563EB]">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section ref={refFaq} className="py-20 md:py-28 px-6 bg-slate-50/80 dark:bg-gray-800/50">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={inViewFaq ? { opacity: 1, y: 0 } : {}} className="text-center mb-12">
            <SectionLabel>FAQ</SectionLabel>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Common Questions</h2>
          </motion.div>

          <div className="space-y-3">
            {faqData.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 12 }}
                animate={inViewFaq ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.06 }}
                className="hero-glass-card rounded-2xl overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex justify-between items-center text-left gap-4"
                >
                  <span className="font-semibold text-slate-900 dark:text-white">{faq.question}</span>
                  <ChevronDownIcon
                    className={`w-5 h-5 text-[#2563EB] flex-shrink-0 transition-transform duration-300 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-5 text-slate-600 dark:text-gray-300 text-sm leading-relaxed">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — matches site original */}
      <section
        ref={refCta}
        className="py-20 px-6 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inViewCta ? { opacity: 1, y: 0 } : {}}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl text-white/90 mb-10">
            Let&apos;s build secure, scalable solutions tailored to your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="px-8 py-4 bg-white text-blue-600 font-medium rounded-lg shadow-lg hover:bg-gray-100 transition">
              Get Started
            </Link>
            <Link to="/portfolio" className="px-8 py-4 border-2 border-white text-white font-medium rounded-lg hover:bg-white/10 transition">
              View Our Work
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}

function ServiceDetailPanel({ service, compact = false }) {
  const Icon = service.icon;
  return (
    <>
      <div className={`relative overflow-hidden ${compact ? 'h-48' : 'h-56 md:h-64'}`}>
        <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-cyan-300">{service.category}</span>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-white">{service.name}</h3>
        </div>
      </div>
      <div className="p-6 md:p-8">
        <p className="text-slate-600 dark:text-gray-300 mb-6 leading-relaxed">{service.fullDescription}</p>
        <div className={`grid gap-6 mb-8 ${compact ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
          <div>
            <h4 className="text-sm font-bold text-[#2563EB] uppercase tracking-wider mb-3">Key Benefits</h4>
            <ul className="space-y-2">
              {service.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-slate-600 dark:text-gray-300">
                  <span className="text-[#2563EB] mt-0.5">✓</span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-bold text-[#2563EB] uppercase tracking-wider mb-3">Capabilities</h4>
            <ul className="space-y-2">
              {service.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-600 dark:text-gray-300">
                  <span className="text-cyan-500 mt-0.5">◆</span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white font-semibold rounded-xl hero-glow hover:-translate-y-0.5 transition-all"
        >
          Request Consultation <ArrowRightIcon className="w-4 h-4" />
        </Link>
      </div>
    </>
  );
}
